import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { KiiBadge } from "@/components/kii/kii-badge";

export interface FilterOption<T = unknown> {
  value: string;
  label: string;
  data?: T;
}

export type FilterColorVariant =
  | "default"
  | "primary"
  | "secondary"
  | "success"
  | "warning"
  | "destructive"
  | "course"
  | "agency"
  | "provider"
  | "teacher"
  | "student"
  | "country";

interface BaseKiiAutoSuggestFilterProps<T = unknown> {
  options?: FilterOption<T>[];
  onSearch?: (query: string) => Promise<FilterOption<T>[]> | FilterOption<T>[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
  colorVariant?: FilterColorVariant;
  label?: string;
  showClearButton?: boolean;
  maxResults?: number;
  renderOption?: (option: FilterOption<T>) => React.ReactNode;
  renderSelected?: (option: FilterOption<T>) => React.ReactNode;
  "data-testid"?: string;
}

interface SingleSelectProps<T = unknown> extends BaseKiiAutoSuggestFilterProps<T> {
  multiple?: false;
  value?: string | null;
  onValueChange: (value: string | null, option?: FilterOption<T>) => void;
}

interface MultiSelectProps<T = unknown> extends BaseKiiAutoSuggestFilterProps<T> {
  multiple: true;
  value?: string[];
  onValueChange: (values: string[], options: FilterOption<T>[]) => void;
}

type KiiAutoSuggestFilterProps<T = unknown> = SingleSelectProps<T> | MultiSelectProps<T>;

const colorVariantClasses: Record<FilterColorVariant, {
  badge: string;
  button: string;
  border: string;
}> = {
  default: {
    badge: "bg-accent text-accent-foreground",
    button: "border-input",
    border: "border-input",
  },
  primary: {
    badge: "bg-primary/10 text-primary border-primary/20",
    button: "border-primary/30",
    border: "border-primary/30",
  },
  secondary: {
    badge: "bg-secondary/10 text-secondary-foreground border-secondary/20",
    button: "border-secondary/30",
    border: "border-secondary/30",
  },
  success: {
    badge: "bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20",
    button: "border-green-500/30",
    border: "border-green-500/30",
  },
  warning: {
    badge: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20",
    button: "border-amber-500/30",
    border: "border-amber-500/30",
  },
  destructive: {
    badge: "bg-destructive/10 text-destructive border-destructive/20",
    button: "border-destructive/30",
    border: "border-destructive/30",
  },
  course: {
    badge: "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20",
    button: "border-blue-500/30",
    border: "border-blue-500/30",
  },
  agency: {
    badge: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20",
    button: "border-amber-500/30",
    border: "border-amber-500/30",
  },
  provider: {
    badge: "bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-500/20",
    button: "border-purple-500/30",
    border: "border-purple-500/30",
  },
  teacher: {
    badge: "bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 border-indigo-500/20",
    button: "border-indigo-500/30",
    border: "border-indigo-500/30",
  },
  student: {
    badge: "bg-cyan-500/10 text-cyan-700 dark:text-cyan-400 border-cyan-500/20",
    button: "border-cyan-500/30",
    border: "border-cyan-500/30",
  },
  country: {
    badge: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20",
    button: "border-emerald-500/30",
    border: "border-emerald-500/30",
  },
};

export function KiiAutoSuggestFilter<T = unknown>(props: KiiAutoSuggestFilterProps<T>) {
  const {
    value,
    onValueChange,
    options: staticOptions = [],
    onSearch,
    placeholder = "Select...",
    searchPlaceholder = "Search...",
    emptyText = "No results found.",
    disabled = false,
    className,
    colorVariant = "default",
    label,
    showClearButton = true,
    maxResults = 10,
    renderOption,
    renderSelected,
    multiple = false,
    "data-testid": dataTestId,
  } = props;

  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [filteredOptions, setFilteredOptions] = React.useState<FilterOption<T>[]>(staticOptions);
  const [isSearching, setIsSearching] = React.useState(false);
  const [selectedOptionsCache, setSelectedOptionsCache] = React.useState<Map<string, FilterOption<T>>>(new Map());

  const colors = colorVariantClasses[colorVariant];

  const selectedValues = multiple
    ? (value as string[] || [])
    : (value ? [value as string] : []);

  const selectedOptions = selectedValues
    .map(val => selectedOptionsCache.get(val))
    .filter((opt): opt is FilterOption<T> => opt !== undefined);

  React.useEffect(() => {
    if (staticOptions.length > 0 && selectedValues.length > 0) {
      const newCache = new Map(selectedOptionsCache);
      let cacheUpdated = false;

      selectedValues.forEach(val => {
        if (!newCache.has(val)) {
          const option = staticOptions.find(opt => opt.value === val);
          if (option) {
            newCache.set(val, option);
            cacheUpdated = true;
          }
        }
      });

      if (cacheUpdated) {
        setSelectedOptionsCache(newCache);
      }
    }
  }, [value, staticOptions]);

  React.useEffect(() => {
    const performSearch = async () => {
      if (onSearch) {
        if (searchQuery.trim()) {
          setIsSearching(true);
        }
        try {
          const results = await onSearch(searchQuery);
          setFilteredOptions(results.slice(0, maxResults));
        } catch (error) {
          console.error("Search error:", error);
          setFilteredOptions([]);
        } finally {
          setIsSearching(false);
        }
      } else {
        const filtered = staticOptions.filter((option) =>
          option.label.toLowerCase().includes(searchQuery.toLowerCase())
        );
        setFilteredOptions(filtered.slice(0, maxResults));
      }
    };

    const debounceTimer = setTimeout(performSearch, 200);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, onSearch, staticOptions, maxResults]);

  const handleSelect = (selectedValue: string) => {
    if (multiple) {
      const currentValues = value as string[] || [];
      const selectedOption = filteredOptions.find(opt => opt.value === selectedValue);

      const isRemoving = currentValues.includes(selectedValue);
      const newValues = isRemoving
        ? currentValues.filter(v => v !== selectedValue)
        : [...currentValues, selectedValue];

      const newCache = new Map(selectedOptionsCache);
      if (isRemoving) {
        newCache.delete(selectedValue);
      } else if (selectedOption) {
        newCache.set(selectedValue, selectedOption);
      }

      setSelectedOptionsCache(newCache);

      const newOptions = newValues
        .map(val => newCache.get(val))
        .filter((opt): opt is FilterOption<T> => opt !== undefined);

      (onValueChange as MultiSelectProps<T>['onValueChange'])(newValues, newOptions);
      setSearchQuery("");
    } else {
      const newValue = selectedValue === value ? null : selectedValue;
      const option = newValue ? filteredOptions.find(opt => opt.value === newValue) : undefined;

      const newCache = new Map(selectedOptionsCache);
      if (newValue && option) {
        newCache.set(newValue, option);
      } else if (!newValue) {
        selectedOptionsCache.forEach((_, key) => newCache.delete(key));
      }
      setSelectedOptionsCache(newCache);

      (onValueChange as SingleSelectProps<T>['onValueChange'])(newValue, option);
      setOpen(false);
      setSearchQuery("");
    }
  };

  const handleRemove = (valueToRemove: string, e?: React.MouseEvent) => {
    e?.stopPropagation();

    if (multiple) {
      const currentValues = value as string[] || [];
      const newValues = currentValues.filter(v => v !== valueToRemove);

      const newCache = new Map(selectedOptionsCache);
      newCache.delete(valueToRemove);
      setSelectedOptionsCache(newCache);

      const newOptions = newValues
        .map(val => newCache.get(val))
        .filter((opt): opt is FilterOption<T> => opt !== undefined);

      (onValueChange as MultiSelectProps<T>['onValueChange'])(newValues, newOptions);
    } else {
      (onValueChange as SingleSelectProps<T>['onValueChange'])(null);
    }
    setSearchQuery("");
  };

  const handleClearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (multiple) {
      setSelectedOptionsCache(new Map());
      (onValueChange as MultiSelectProps<T>['onValueChange'])([], []);
    } else {
      (onValueChange as SingleSelectProps<T>['onValueChange'])(null);
    }
    setSearchQuery("");
  };

  const hasSelection = selectedValues.length > 0;

  const getButtonText = () => {
    if (!hasSelection) return placeholder;

    if (multiple) {
      return `${placeholder} (${selectedValues.length} selected)`;
    } else {
      const selectedOption = selectedOptions[0];
      if (selectedOption) {
        return renderSelected ? renderSelected(selectedOption) : selectedOption.label;
      }
      return placeholder;
    }
  };

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <label className="text-sm font-medium text-foreground">
          {label}
        </label>
      )}
      <div className="space-y-2">
        <Popover open={open} onOpenChange={setOpen}>
          <div className="relative">
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className={cn(
                  "w-full justify-between",
                  colors.button,
                  hasSelection && showClearButton && "pr-16"
                )}
                disabled={disabled}
                data-testid={dataTestId}
              >
                <span className={cn(
                  "truncate",
                  hasSelection ? "text-foreground" : "text-muted-foreground"
                )}>
                  {getButtonText()}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            {showClearButton && hasSelection && !disabled && (
              <button
                type="button"
                className="absolute right-9 top-1/2 -translate-y-1/2 h-5 w-5 rounded-sm hover:bg-black/10 dark:hover:bg-white/10 flex items-center justify-center z-10"
                onClick={handleClearAll}
                data-testid={`${dataTestId}-clear`}
                aria-label="Clear all selections"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <PopoverContent className="w-full p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder={searchPlaceholder}
                value={searchQuery}
                onValueChange={setSearchQuery}
              />
              <CommandList>
                {isSearching ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    Searching...
                  </div>
                ) : filteredOptions.length === 0 ? (
                  <CommandEmpty>{emptyText}</CommandEmpty>
                ) : (
                  <CommandGroup>
                    {filteredOptions.map((option) => {
                      const isSelected = selectedValues.includes(option.value);
                      return (
                        <CommandItem
                          key={option.value}
                          value={option.value}
                          onSelect={() => handleSelect(option.value)}
                          data-testid={`filter-option-${option.value}`}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              isSelected ? "opacity-100" : "opacity-0"
                            )}
                          />
                          {renderOption ? renderOption(option) : option.label}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {multiple && hasSelection && (
          <div className="flex flex-wrap gap-1.5">
            {selectedOptions.map((option) => (
              <KiiBadge
                key={option.value}
                variant="outline"
                className={cn(
                  "font-normal pl-2 pr-1 py-1 gap-1 flex items-center",
                  colors.badge
                )}
                data-testid={`selected-${option.value}`}
              >
                <span className="text-xs">
                  {renderSelected ? renderSelected(option) : option.label}
                </span>
                <button
                  type="button"
                  className="h-3 w-3 rounded-sm hover:bg-black/10 dark:hover:bg-white/10 flex items-center justify-center ml-1"
                  onClick={(e) => handleRemove(option.value, e)}
                  data-testid={`remove-${option.value}`}
                  aria-label={`Remove ${option.label}`}
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </KiiBadge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
